<template>
    <v-sheet width="100%" class="ma-2 bg-transparent">
        <div class="d-flex align-start" style="gap: 8px">
        <v-select v-model="selectedTargetIds" :items="targetList" label="Target Entities (Select Multiple)" :item-title="vv => {
            const spawnStr = vv.appearAt > 0 ? new Date(vv.appearAt * 1000).toLocaleTimeString([], { hour12: false }) : '?';
            const dieStr = vv.dieAt > 0 ? new Date(vv.dieAt * 1000).toLocaleTimeString([], { hour12: false }) : 'alive';
            const bossTag = trackedBosses.length > 0 && trackedBosses.includes(vv.raceId) ? '⭐ ' : '';
            return `${bossTag}${vv.name} - ${(vv.total || 0).toLocaleString()} (${spawnStr} → ${dieStr})`;
        }" :item-value="vv => vv.id" density="compact" variant="outlined" multiple chips closable-chips>
            <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 3">
                    {{ item.title }}
                </v-chip>
                <span v-if="index === 3" class="text-grey text-caption align-self-center">
                    (+{{ selectedTargetIds.length - 3 }} others)
                </span>
            </template>
        </v-select>
            <!-- Admin only: start a new live session without restarting the parser -->
            <v-tooltip v-if="(currentUser?.role || 0) >= 3" location="bottom">
                <template #activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-plus-box-multiple-outline" variant="text" color="primary"
                        class="mt-1" @click="newSessionError = ''; newSessionDialog = true" />
                </template>
                New Session
            </v-tooltip>
        </div>

        <v-dialog v-model="newSessionDialog" max-width="440">
            <v-card title="Start a new session?">
                <v-card-text>
                    The live session ends here and capture continues into a new one, without restarting the parser.
                    Players and entities in view are carried over.
                    <div v-if="newSessionError" class="text-error mt-2">{{ newSessionError }}</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="newSessionDialog = false">Cancel</v-btn>
                    <v-btn color="primary" variant="flat" :loading="newSessionBusy" @click="startNewSession">Start new session</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar v-model="newSessionToast" timeout="4000" color="success">
            New session started: {{ newSessionTable }}
        </v-snackbar>

        <v-divider class="my-3" />

        <!-- Combat Timeline Slider -->
        <combat-timeline
            v-if="combatBounds && selectedTargetIds.length > 0"
            :target-ids="selectedTargetIds"
            :combat-start="combatBounds.start"
            :combat-last="combatBounds.end"
            :status803-intervals="status803Intervals"
            :target-dead="targetDead"
            @update:range="fetchSlice"
            class="mx-0 mb-2"
        />

        <v-card v-if="targetGroupStats" class="mb-4" color="surface-variant" variant="outlined">
            <v-card-text class="d-flex justify-space-between align-center py-2">
                <div>
                    <div class="text-overline mb-0">Raid Summary ({{ selectedTargetIds.length }} Targets, {{ playerStats.length }} Players)</div>
                    <div class="text-h6 font-weight-bold">
                        {{ formatNumber(targetGroupStats.totalDamage) }} dmg
                    </div>
                    <div v-if="primaryTargetHp" class="text-caption text-grey-lighten-1">
                        {{ primaryTargetName }} HP: {{ formatNumber(primaryTargetHp.current) }} / {{ formatNumber(primaryTargetHp.max) }}
                    </div>
                </div>
                <div class="d-flex flex-column align-end" style="gap: 4px;">
                    <div class="text-right">
                        <div class="text-caption text-grey-lighten-1">Raid Effective DPS</div>
                        <div class="text-h6 font-weight-bold">{{ formatNumber(targetGroupStats.effectiveDPS) }}</div>
                        <div class="text-caption text-grey-lighten-1">
                            Time: {{ formatDuration(targetGroupStats.totalTime) }} (Eff: {{
                                formatDuration(targetGroupStats.effectiveTime) }})
                        </div>
                        <div class="text-caption mt-1">
                            Avg DPS: {{ formatNumber(targetGroupStats.raidDPS) }}
                        </div>
                    </div>
                    <v-btn v-if="selectedTargetIds.length > 0 && playerStats.length > 0"
                        size="small" variant="tonal" color="blue-lighten-2" class="mt-1"
                        prepend-icon="mdi-format-list-bulleted"
                        @click="hitLogOpen = true">
                        Hit Log
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>
        
        <!-- Musical Buffs Summary (Detected Musical Buffs across Party) -->
        <div v-if="raidMusicalBuffs.length > 0" class="d-flex flex-wrap align-center mb-4 px-2" style="gap: 12px;">
            <div v-for="buff in raidMusicalBuffs" :key="buff.id" class="d-flex align-center bg-grey-darken-4 pa-2 rounded-lg border border-grey-darken-2" style="min-width: 120px;">
                <v-avatar size="32" rounded class="mr-2">
                    <v-img :src="getStatusIconUrl(buff.id)" cover>
                        <template v-slot:placeholder>
                            <v-icon icon="mdi-music-note" size="20"></v-icon>
                        </template>
                    </v-img>
                </v-avatar>
                <div class="d-flex flex-column">
                    <span class="text-caption text-grey-lighten-2 font-weight-medium text-truncate" style="font-size: 0.65rem; max-width: 100px;">{{ buff.name }}</span>
                    <span v-if="buff.maxPercent !== undefined" class="text-subtitle-1 font-weight-black text-blue-lighten-4" style="line-height: 1.1;">Max: {{ buff.maxPercent.toFixed(2) }}%</span>
                    <div v-if="buff.magicAttack !== undefined || buff.alchemicDamage !== undefined || buff.magicCastingSpeed !== undefined || buff.alchemyCastingSpeed !== undefined" class="d-flex flex-column mt-1" style="gap: 2px;">
                        <span v-if="buff.magicAttack !== undefined || buff.alchemicDamage !== undefined" class="text-caption text-blue-lighten-4 font-weight-bold" style="line-height: 1; font-size: 0.70rem !important;">
                            <template v-if="buff.magicAttack !== undefined">MA: {{ buff.magicAttack.toFixed(2) }}% </template>
                            <template v-if="buff.alchemicDamage !== undefined">AD: {{ buff.alchemicDamage.toFixed(2) }}%</template>
                        </span>
                        <span v-if="buff.magicCastingSpeed !== undefined || buff.alchemyCastingSpeed !== undefined" class="text-caption text-purple-lighten-4 font-weight-bold" style="line-height: 1; font-size: 0.70rem !important;">
                            <template v-if="buff.magicCastingSpeed !== undefined">MC: {{ buff.magicCastingSpeed.toFixed(2) }}% </template>
                            <template v-if="buff.alchemyCastingSpeed !== undefined">AC: {{ buff.alchemyCastingSpeed.toFixed(2) }}%</template>
                        </span>
                    </div>
                </div>
                <v-tooltip activator="parent" location="bottom">
                    <div class="d-flex flex-column">
                        <span v-if="buff.maxPercent !== undefined">Max Damage: {{ buff.maxPercent.toFixed(2) }}%</span>
                        <span v-if="buff.magicAttack !== undefined">Magic Attack: {{ buff.magicAttack.toFixed(2) }}%</span>
                        <span v-if="buff.alchemicDamage !== undefined">Alchemic Damage: {{ buff.alchemicDamage.toFixed(2) }}%</span>
                        <span v-if="buff.magicCastingSpeed !== undefined">Magic Casting Speed: {{ buff.magicCastingSpeed.toFixed(2) }}%</span>
                        <span v-if="buff.alchemyCastingSpeed !== undefined">Alchemy Casting Speed: {{ buff.alchemyCastingSpeed.toFixed(2) }}%</span>
                    </div>
                </v-tooltip>
            </div>
        </div>

        <!-- Hit Log Modal -->
        <damage-list
            v-model="hitLogOpen"
            :target-name="primaryTargetName || selectedTargetIds[0] || ''"
            :target-id="selectedTargetIds[0] || ''"
            :players="playerStats.map(p => ({ id: p.id, name: p.name }))"
            :from-time="activeSlice?.from ?? undefined"
            :to-time="activeSlice?.to ?? undefined"
            :combat-start="combatBounds?.start ?? 0"
        />

        <!-- Target Status Summary (Decomposed) -->
        <target-status-coverage v-if="selectedTargetIds.length > 0 && targetStatusStats.length > 0"
            :stats="targetStatusStats" :primary-target-name="primaryTargetName" :format-duration="formatDuration"
            :get-status-icon-url="getStatusIconUrl" />


        <v-sheet class="bg-transparent px-0">
            <!-- Table Headers -->
            <v-row class="py-2 text-grey-lighten-2 font-weight-bold" no-gutters
                style="font-size: 0.8rem; letter-spacing: 0.025rem; text-transform: uppercase;">
                <v-col cols="1" class="text-center pl-4">Rank</v-col>
                <v-col v-if="!anonymizeMode" cols="2" class="text-center">Player</v-col>
                <v-col cols="1" class="text-center">Arcana</v-col>
                <v-col :cols="anonymizeMode ? 4 : 2" class="text-center">Weapon</v-col>
                <v-col cols="1" class="text-center">Damage</v-col>
                <v-col cols="1" class="text-center">Avg DPS</v-col>
                <v-col cols="1" class="text-center">Act DPS</v-col>
                <v-col cols="1" class="text-center">Eff DPS</v-col>
                <v-col cols="1" class="text-center">Crit Rate</v-col>
                <v-col cols="1" class="text-center pr-4">Active Time</v-col>
            </v-row>

            <v-expansion-panels multiple variant="popout">
                <v-expansion-panel v-for="(player, index) in playerStats" :key="player.id"
                    class="mb-2 bg-grey-darken-4 overflow-hidden rounded" style="border: none;">

                    <v-expansion-panel-title class="pa-0 share-row" hide-actions>
                        <!-- Damage share: a thin bar along the bottom, not a fill behind the text -->
                        <div class="share-bar" :style="{ width: `${player.percent}%`, background: getPlayerColor(index) }"></div>
                        <v-row no-gutters class="align-center py-3" style="width: 100%;">
                            <!-- Rank -->
                            <v-col cols="1" class="text-center pl-4">
                                <span class="text-body-2 font-weight-bold text-white"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">#{{ index + 1 }}</span>
                            </v-col>
                            <!-- Player -->
                            <v-col v-if="!anonymizeMode" cols="2" class="text-center">
                                <strong class="text-body-1 text-white"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">{{
                                        player.name
                                    }}</strong>
                            </v-col>
                            <!-- Arcana -->
                            <v-col cols="1" class="text-center">
                                <span v-if="getPrimaryClass(player.entity)"
                                    class="text-white font-weight-bold text-body-2"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">
                                    {{ getPrimaryClass(player.entity) }}
                                </span>
                                <span v-else class="text-grey-darken-1">-</span>
                            </v-col>
                            <!-- Weapon -->
                            <v-col :cols="anonymizeMode ? 4 : 2" class="text-center">
                                <span v-if="getPrimaryWeapon(player.entity)"
                                    class="text-white font-weight-bold text-body-2 d-block"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9); white-space: pre-line; line-height: 1.2;">
                                    {{ getPrimaryWeapon(player.entity) }}
                                </span>
                                <span v-else class="text-grey-darken-1">-</span>
                            </v-col>
                            <!-- Damage -->
                            <v-col cols="1" class="text-center d-flex flex-column justify-center align-center">
                                <span class="font-weight-bold text-white text-body-2"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9); line-height: 1.2;">
                                    {{ formatNumber(player.totalDamage) }}
                                </span>
                                <span class="text-caption text-grey-lighten-1 mt-1"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9); line-height: 1;">
                                    {{ player.percent.toFixed(1) }}%
                                </span>
                            </v-col>
                            <!-- Avg DPS -->
                            <v-col cols="1" class="text-center">
                                <span class="text-body-2 text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">{{
                                    formatNumber(player.averageDPS) }}</span>
                            </v-col>
                            <!-- Active DPS -->
                            <v-col cols="1" class="text-center">
                                <span class="text-body-2 text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">{{
                                    formatNumber(player.activeDPS) }}</span>
                            </v-col>
                            <!-- Eff DPS -->
                            <v-col cols="1" class="text-center">
                                <span class="font-weight-bold text-white text-body-2"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">{{ formatNumber(player.effectiveDPS)
                                    }}</span>
                            </v-col>
                            <!-- Crit Rate -->
                            <v-col cols="1" class="text-center">
                                <span class="text-amber-lighten-1 font-weight-bold text-body-2"
                                    style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">{{ player.critRate.toFixed(1)
                                    }}%</span>
                            </v-col>
                            <!-- Active Time -->
                            <v-col cols="1" class="text-center pr-4">
                                <span class="text-body-2 text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.9);">{{
                                    formatDuration(player.effectiveTime) }}</span>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text class="bg-grey-darken-4 pa-0">
                        <v-row no-gutters class="pa-4">
                            <!-- Left: Statuses -->
                            <v-col cols="12" md="auto" v-if="player.statusStats?.length > 0">
                                <self-status-list :statuses="player.statusStats" title="Self Statuses"
                                    :format-duration="formatDuration" :get-status-icon-url="getStatusIconUrl" />
                            </v-col>

                            <!-- Right: Skills Bar Graph -->
                            <v-col cols="12" md style="min-width: 0">
                                <skill-breakdown :skills="player.skillStats" title="Skill Breakdown"
                                    :active-damage="player.activeDamage" :passive-damage="player.passiveDamage" :pet-damage="player.petDamage"
                                    :format-number="formatNumber" :get-skill-icon-url="getSkillIconUrl"
                                    :get-status-icon-url="getStatusIconUrl"
                                    :get-skill-color="getSkillColor" :hex-to-rgba="hexToRgba" :region="region" />
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-sheet>

        <condition-windows :target-ids="selectedTargetIds" :from-time="activeSlice?.from ?? undefined"
            :to-time="activeSlice?.to ?? undefined" :combat-start="combatBounds?.start ?? 0"
            :player-order="playerStats.map(p => p.id)" />
    </v-sheet>
</template>

<script lang="ts">
import { defineComponent, inject, ref, computed, onMounted, onUnmounted, markRaw, watch, watchEffect, nextTick, Ref, toRaw } from 'vue';
import { ActorManager, EntityActor } from '@/eventActor';
import { trackedStatuses, getPrimaryClass, getPrimaryWeapon, anonymizeMode, isLoading, optimizationMode, statusGroups, analysisFilters, trackedBosses, currentSessionName, currentUser } from "@/store";
import { getSkillIconUrl, getStatusIconUrl, getPlayerColor, getSkillColor, hexToRgba, formatNumber, formatDuration } from "@/util";
import { calculateStatusStatsMainThread } from '@/statusUtils';
import TargetStatusCoverage from './TargetStatusCoverage.vue';
import SelfStatusList from './SelfStatusList.vue';
import SkillBreakdown from './SkillBreakdown.vue';
import CombatTimeline from './CombatTimeline.vue';
import { ampCoverage, mergeAmp } from '@/skillAmp';
import DamageList from './subComponents/damageList.vue';
import ConditionWindows from './ConditionWindows.vue';
import { PASSIVE_SKILL_IDS } from '@/passiveSkills';

export default defineComponent({
    name: "ApplyDamageAnalysis",
    components: {
        TargetStatusCoverage,
        SelfStatusList,
        SkillBreakdown,
        CombatTimeline,
        DamageList,
        ConditionWindows
    },
    setup() {
        console.log("ApplyDamageAnalysis setup started");
        const actorManager = inject('actorManager') as Ref<ActorManager>;
        const skillNameMap = inject('skillNameMap') as Ref<Record<number, string>>;
        const condNameMap = inject('condNameMap') as Ref<Record<number, string>>;
        const raceNameMap = inject('raceNameMap') as Ref<Record<number, string>>;
        const region = inject('region') as unknown as string;
        const appEvent = inject('appEvent') as Ref<EventTarget>;
        const db = inject('db') as any;

        onMounted(() => {
            appEvent.value.addEventListener('clear', onClearHandler);

            // Lazy Load Resources (Non-blocking)
            // Guard: If data is already loaded in the Store (singleton), DON'T overwrite it.
            // This prevents forced reactivity updates which cause v-img flash/blank.
            if (Object.keys(skillNameMap.value).length > 0 && Object.keys(condNameMap.value).length > 0) {
                // Already loaded, do nothing
                return;
            }

            // Even if App.vue loaded them, we check again to be safe/reactive
            Promise.all([
                db.value.getData('SkillNameMap'),
                db.value.getData('StatusNameMap'),
                db.value.getData('RaceNameMap')
            ]).then(([localSkillMap, localCondMap, localRaceMap]) => {
                // Double check inside promise in case multiple components ran this
                if (localSkillMap && Object.keys(skillNameMap.value).length === 0) Object.assign(skillNameMap.value, localSkillMap);
                if (localCondMap && Object.keys(condNameMap.value).length === 0) Object.assign(condNameMap.value, localCondMap);
                if (localRaceMap && Object.keys(raceNameMap.value).length === 0) {
                    for (const [id, name] of Object.entries(localRaceMap)) {
                        raceNameMap.value[+id] = `${name} ${id}`;
                    }
                }
            }).catch((e: any) => console.error("Failed to lazy load analysis resources", e));
        });

        const selectedTargetIds = ref<string[]>([]);
        const generationCounter = ref(0); // Increment on clear to abort ongoing calculations

        const onClear = () => {
            console.log('Analysis: onClear triggering');
            selectedTargetIds.value = [];
            generationCounter.value++; // Invalidate any ongoing async computations
            targetWindowStatusStats.value = null;
        };

        const onClearHandler = () => {
            onClear();
            // Also clear the timeline slice state
            activeSlice.value = null;
        };

        // â”€â”€ Timeline Slice State â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        // Absolute combat bounds (populated from original non-sliced data to prevent UI layout shift)
        const combatBounds = computed(() => {
            // Absolute session bounds from backend
            let start = (actorManager.value as any).combatStart || (actorManager.value as any).CombatStart || 0;
            let end = (actorManager.value as any).combatLast || (actorManager.value as any).CombatLast || 0;

            // If we have selected targets, we might want to prioritize their damage window if they are "partial"
            // but for the SLIDER, we want the most inclusive range possible.
            const players = Object.values(actorManager.value.entityMap).filter((e: any) => e.isPC || e.IsPC);
            for (const p of players) {
                const agg = (p as any).aggregatedStats || (p as any).AggregatedStats;
                if (!agg) continue;
                for (const tid of selectedTargetIds.value) {
                    const tStats = agg[tid];
                    if (tStats) {
                        const first = tStats.firstAttackAt ?? tStats.FirstAttackAt;
                        const last = tStats.lastAttackAt ?? tStats.LastAttackAt;
                        if (start === 0 || first < start) start = first;
                        if (last === 0 || last > end) end = last;
                    }
                }
            }

            if (start === 0 || end === 0) return null;
            return { start, end };
        });

        // Whether the primary selected target is confirmed dead
        const targetDead = computed(() => {
            for (const id of selectedTargetIds.value) {
                const e = actorManager.value.entityMap[id];
                if (e) {
                    const deadAt = (e as any).disappearAt || (e as any).DisappearAt || 0;
                    if (deadAt > 0) return true;
                }
            }
            return false;
        });

        // Status 803 intervals merged from all selected entities (always full duration for the UI overlay)
        const status803Intervals = computed(() => {
            const result: { start: number, end: number }[] = [];
            for (const id of selectedTargetIds.value) {
                const e = actorManager.value.entityMap[id];
                if (!e) continue;
                const tl = (e as any).statusTimelines || (e as any).StatusTimelines || {};
                const ivs = tl[803] || tl["803"];
                if (ivs) result.push(...ivs);
            }
            return result;
        });

        // The active time slice: null = full session, { from, to } = partial
        const activeSlice = ref<{ from: number; to: number } | null>(null);
        const sliceLoading = ref(false);
        // Sliced payload from server (only set when a slice is active)
        const slicedPayload = ref<any>(null);

        // Transparently swaps the entity map if a slice is active
        const activeEntityMap = computed(() => {
            if (activeSlice.value && slicedPayload.value) {
                const players = slicedPayload.value.playerStats || slicedPayload.value.PlayerStats || {};
                const targets = slicedPayload.value.targetStats || slicedPayload.value.TargetStats || {};
                return {
                    ...players,
                    ...targets
                };
            }
            return actorManager.value.entityMap;
        });

        // â”€â”€ Timeline Slice Fetch Logic â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        let sliceTimer: ReturnType<typeof setTimeout> | null = null;
        let abortController: AbortController | null = null;

        const fetchSlice = (range: { from: number; to: number } | null) => {
            if (sliceTimer) clearTimeout(sliceTimer);
            if (abortController) abortController.abort();

            if (!range) {
                activeSlice.value = null;
                slicedPayload.value = null;
                return;
            }

            activeSlice.value = range;
            sliceLoading.value = true;
            abortController = new AbortController();
            const signal = abortController.signal;

            sliceTimer = setTimeout(async () => {
                try {
                    const sessionName = currentSessionName.value;
                    let url: string;
                    if (sessionName) {
                        url = `/api/session/analysis?target=${encodeURIComponent(sessionName)}&from=${range.from}&to=${range.to}`;
                    } else {
                        url = `/api/combat/slice?from=${range.from}&to=${range.to}`;
                    }

                    const resp = await fetch(url, { signal });
                    if (resp.ok) {
                        const data = await resp.json();
                        if (!signal.aborted) {
                            slicedPayload.value = data;
                        }
                    }
                } catch (e: any) {
                    if (e.name !== 'AbortError') {
                        console.warn('Timeline slice fetch failed', e);
                    }
                } finally {
                    if (!signal.aborted) {
                        sliceLoading.value = false;
                    }
                }
            }, 300);
        };

        // Reset slice when selection changes
        watch(selectedTargetIds, () => {
            activeSlice.value = null;
            slicedPayload.value = null;
        });

        // â”€â”€ Boss-window status stats override â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        // The backend pre-computes player.statusStats over the entire combat session
        // [CombatStart, CombatLast]. When the user selects a specific boss, those
        // numbers are wrong (uptime spans full session, not the boss raid window).
        // We fetch a re-bounded version from the server keyed to selectedTargetIds
        // and have playerStats prefer this map when present.
        //
        // Live mode polls every TARGET_STATUS_POLL_MS so the panel updates without a
        // page refresh â€” same pattern hitlog uses (see damageList.vue). Session mode
        // is static, so we fetch once on selection change.
        const TARGET_STATUS_POLL_MS = 1000;
        const targetWindowStatusStats = ref<Record<string, any[]> | null>(null);
        let targetStatusFetchTimer: ReturnType<typeof setTimeout> | null = null;
        let targetStatusAbort: AbortController | null = null;
        let targetStatusPollInterval: ReturnType<typeof setInterval> | null = null;

        const runTargetWindowFetch = async (targetIds: string[]) => {
            if (targetStatusAbort) targetStatusAbort.abort();
            if (targetIds.length === 0) {
                targetWindowStatusStats.value = null;
                return;
            }
            targetStatusAbort = new AbortController();
            const signal = targetStatusAbort.signal;
            try {
                const sessionName = currentSessionName.value;
                const idsParam = encodeURIComponent(targetIds.join(','));
                const url = sessionName
                    ? `/api/session/analysis?target=${encodeURIComponent(sessionName)}&targetIds=${idsParam}`
                    : `/api/combat/status-stats?targetIds=${idsParam}`;

                const resp = await fetch(url, { signal });
                if (!resp.ok || signal.aborted) return;
                const data = await resp.json();

                // Live endpoint returns { playerStats: { id: stats[] } }.
                // Session endpoint returns the full LiveSync payload â€” extract per-player StatusStats.
                const map: Record<string, any[]> = {};
                if (data && data.playerStats) {
                    for (const [pid, val] of Object.entries(data.playerStats)) {
                        if (Array.isArray(val)) {
                            map[pid] = val;
                        } else if (val && Array.isArray((val as any).statusStats)) {
                            map[pid] = (val as any).statusStats;
                        } else if (val && Array.isArray((val as any).StatusStats)) {
                            map[pid] = (val as any).StatusStats;
                        }
                    }
                }
                if (!signal.aborted) {
                    targetWindowStatusStats.value = map;
                }
            } catch (e: any) {
                if (e.name !== 'AbortError') {
                    console.warn('Target-window status-stats fetch failed', e);
                }
            }
        };

        // Debounced fetch for selection-change events (avoids hammering on rapid clicks)
        const fetchTargetWindowStatusStats = (targetIds: string[]) => {
            if (targetStatusFetchTimer) clearTimeout(targetStatusFetchTimer);
            if (targetIds.length === 0) {
                if (targetStatusAbort) targetStatusAbort.abort();
                targetWindowStatusStats.value = null;
                return;
            }
            targetStatusFetchTimer = setTimeout(() => runTargetWindowFetch(targetIds), 300);
        };

        const ensureTargetStatusPolling = () => {
            const shouldPoll = !currentSessionName.value
                && selectedTargetIds.value.length > 0
                && !activeSlice.value;

            if (shouldPoll && !targetStatusPollInterval) {
                targetStatusPollInterval = setInterval(() => {
                    runTargetWindowFetch([...selectedTargetIds.value]);
                }, TARGET_STATUS_POLL_MS);
            } else if (!shouldPoll && targetStatusPollInterval) {
                clearInterval(targetStatusPollInterval);
                targetStatusPollInterval = null;
            }
        };

        watch(selectedTargetIds, (ids) => {
            // Skip when a manual slice is active â€” the slicedPayload already carries
            // correctly-bounded per-entity StatusStats for that window.
            if (activeSlice.value) {
                targetWindowStatusStats.value = null;
                ensureTargetStatusPolling();
                return;
            }
            fetchTargetWindowStatusStats([...ids]);
            ensureTargetStatusPolling();
        }, { immediate: true });

        // Re-evaluate when a manual slice toggles on/off
        watch(activeSlice, (val) => {
            if (val) {
                targetWindowStatusStats.value = null;
            } else if (selectedTargetIds.value.length > 0) {
                fetchTargetWindowStatusStats([...selectedTargetIds.value]);
            }
            ensureTargetStatusPolling();
        });

        // Stop the poll when leaving session mode (entering live) or vice versa
        watch(currentSessionName, () => {
            ensureTargetStatusPolling();
        });

        onUnmounted(() => {
            if (targetStatusFetchTimer) clearTimeout(targetStatusFetchTimer);
            if (targetStatusAbort) targetStatusAbort.abort();
            if (targetStatusPollInterval) clearInterval(targetStatusPollInterval);
        });

        // Table headers for player breakdown
        const playerHeaders = [
            { title: '#', key: 'rank', sortable: false, width: '40px' },
            { title: 'Player', key: 'player', sortable: false, width: '15%' },
            { title: 'Class', key: 'class', sortable: false, width: '10%' },
            { title: 'Weapon', key: 'weapon', sortable: false, width: '12%' },
            { title: 'Damage', key: 'damage', sortable: false, width: '18%' },
            { title: 'Avg DPS', key: 'avgDPS', sortable: false, width: '9%' },
            { title: 'Active DPS', key: 'activeDPS', sortable: false, width: '9%' },
            { title: 'Eff DPS', key: 'effectiveDPS', sortable: false, width: '9%' },
            { title: 'Active DPS Time', key: 'time', sortable: false, width: '8%' }
        ];



        onUnmounted(() => {
            appEvent.value.removeEventListener('clear', onClearHandler);
        });

        // Helper to get display name
        const getDisplayName = (entity: any) => {
            return entity.name || entity.Name;
        };

        // Helper to calculate effective time blocks
        function getEffectiveIntervals(damages: { At: number }[]) {
            if (damages.length === 0) return [];

            const intervals: { start: number, end: number }[] = [];
            const GAP = 3.0;

            let blockStart = damages[0].At;
            let lastAt = damages[0].At;

            for (let i = 1; i < damages.length; i++) {
                const d = damages[i];
                if (d.At - lastAt > GAP) {
                    const duration = Math.max(1, lastAt - blockStart);
                    intervals.push({ start: blockStart, end: blockStart + duration });
                    blockStart = d.At;
                }
                lastAt = d.At;
            }
            const duration = Math.max(1, lastAt - blockStart);
            intervals.push({ start: blockStart, end: blockStart + duration });

            return intervals;
        }

        // List entities (non-PC only), sorted by total damage descending
        const targetList = computed(() => {
            // Track liveUpdate to force re-evaluation only when actual data changes
            actorManager.value.liveUpdate.value;
            if (isLoading.value) return [];

            const list: { id: string, name: string, total: number, raceId: number, isPC: boolean, lastAt: number, appearAt: number, dieAt: number }[] = [];
            const em = actorManager.value.entityMap;
            const minDamage = analysisFilters.value.minEntityDamage || 0;
            const minDebuff = analysisFilters.value.minDebuffCount || 0;
            const tracked = new Set(trackedStatuses.value);

            for (const key in em) {
                const entity = em[key];
                if (!entity) continue;

                const total = entity.totalTakeDamage || 0;
                const raceId = entity.raceId || 0;
                const isTrackedBoss = trackedBosses.value.includes(raceId);
                const isSelected = selectedTargetIds.value.includes(key);

                // For regular entities (non-bosses and not currently selected), apply filters
                if (!isTrackedBoss && !isSelected) {
                    if (total < minDamage) continue;
                    
                    if (entity.isPC) continue;
                    if (entity.ownerId && entity.ownerId !== "0") {
                        const owner = em[entity.ownerId];
                        if (owner && owner.isPC) continue;
                    }

                    // Filter by minimum tracked debuff status count
                    if (minDebuff > 0) {
                        const timelines = (entity as any).statusTimelines || (entity as any).StatusTimelines || {};
                        const trackedDebuffCount = Object.keys(timelines).filter(k => tracked.has(+k)).length;
                        if (trackedDebuffCount < minDebuff) continue;
                    }
                }

                const raceName = raceNameMap.value[raceId];
                const entityName = raceName ? raceName : `Unknown (${raceId})`;
                const disappearAt = (entity as any).disappearAt || (entity as any).DisappearAt || 0;
                const dieAt = disappearAt > 0 ? disappearAt : (entity.lastTakeDamageAt || 0);

                list.push({
                    id: entity.id || key,
                    name: entityName,
                    total: total,
                    raceId: raceId,
                    isPC: false,
                    lastAt: dieAt,
                    appearAt: entity.appearAt || 0,
                    dieAt,
                });
            }

            return markRaw(list.sort((a, b) => {
                if (b.dieAt !== a.dieAt) return b.dieAt - a.dieAt;
                return b.total - a.total;
            }));
        });

        // Auto-clear selection when list becomes empty (fail-safe)
        watch(targetList, (newList) => {
            if (newList.length === 0 && selectedTargetIds.value.length > 0) {
                console.log('Analysis: targetList empty, force clearing selection');
                selectedTargetIds.value = [];
            }
        });

        const primaryTargetName = computed(() => {
            if (selectedTargetIds.value.length === 0) return 'None';
            if (selectedTargetIds.value.length > 1) return 'Combined Targets';

            let max = -1;
            let target: any = null;
            for (const id of selectedTargetIds.value) {
                const e = activeEntityMap.value[id];
                if (e && e.totalTakeDamage > max) {
                    max = e.totalTakeDamage;
                    target = e;
                }
            }
            if (!target) return 'None';
            if (target.isPC) return getDisplayName(target);
            const raceName = raceNameMap.value[target.raceId];
            return raceName ? raceName : target.name;
        });

        const raidStats = computed(() => {
            if (selectedTargetIds.value.length === 0) return null;

            // Identify Primary Boss for Cutoff and Status
            let maxDmg = -1;
            let primaryTarget: any = null;
            const targetSet = new Set(selectedTargetIds.value);
            const selectedEntities: any[] = [];

            for (const id of selectedTargetIds.value) {
                const e = activeEntityMap.value[id];
                if (e) {
                    selectedEntities.push(e);
                    if (e.totalTakeDamage > maxDmg) {
                        maxDmg = e.totalTakeDamage;
                        primaryTarget = e;
                    }
                }
            }

            const players = Object.values(activeEntityMap.value).filter((e: any) => (e.isPC || e.IsPC));
            let totalDamage = 0;
            let startTime = Number.MAX_VALUE;
            let endTime = 0;
            // Aggregated Timeline construction
            const combinedTimeline: Record<number, number> = {};

            // Optimization Path: Use Aggregated Stats
            for (const p of (players as any[])) {
                const agg = p.aggregatedStats || p.AggregatedStats;
                if (!agg) continue;

                for (const tid of selectedTargetIds.value) {
                    const tStats = agg[tid];
                    if (tStats) {
                        const total = tStats.totalDamage ?? tStats.TotalDamage ?? 0;
                        const first = tStats.firstAttackAt ?? tStats.FirstAttackAt;
                        const last = tStats.lastAttackAt ?? tStats.LastAttackAt;
                        const timeline = tStats.timeline || tStats.Timeline;

                        totalDamage += total;
                        if (first < startTime) startTime = first;
                        if (last > endTime) endTime = last;

                        // Merge timelines
                        if (timeline) {
                            for (const [sec, dmg] of Object.entries(timeline) as [string, number][]) {
                                combinedTimeline[+sec] = (combinedTimeline[+sec] || 0) + dmg;
                            }
                        }
                    }
                }
            }

                // Refinement 135: Stricter Effective Time
                // Instead of merging gaps, we count unique seconds where:
                // 1. Damage was done (combinedTimeline[sec] > 0)
                // 2. Targets were NOT invincible (Status 494)

                const seconds = Object.keys(combinedTimeline).map(Number).sort((a, b) => a - b);

                // Helper to check if ALL selected targets are invincible at a given time
                const isTargetInvincible = (at: number) => {
                    if (selectedEntities.length === 0) return false;
                    return selectedEntities.every((e: any) => {
                        if (e.getConditionState) {
                            const states = e.getConditionState(at);
                            return states.some((c: any) => c.CCId === 494);
                        }
                        const tls = (e.statusTimelines || e.StatusTimelines) || {};
                        const ivs = tls[494] || tls["494"];
                        if (!ivs) return false;
                        return ivs.some((iv: any) => at >= iv.start && at <= iv.end);
                    });
                };

            const effectiveSeconds = seconds.filter(sec => !isTargetInvincible(sec));
            const effectiveTime = Math.max(0.1, effectiveSeconds.length);

            // Construct intervals for the chart (still needed for some UI elements if any, 
            // but we make them 1s blocks for consistency with the new logic)
            const intervals = effectiveSeconds.map((sec: number) => ({ start: sec, end: sec + 1 }));

            // Raid DPS Approx (from Raw Range)
            const rawDuration = Math.max(0.1, (endTime - startTime) || 0);
            const raidDPS = Math.round(totalDamage / rawDuration);

            // If we have selected targets but no damage yet (start of raid or empty slice),
            // return a zeroed stats object instead of null to keep the UI visible.
            const result = {
                primaryTarget: primaryTarget || { name: 'None' },
                selectedEntities,
                totalDamage,
                totalTime: rawDuration,
                startTime: startTime === Number.MAX_VALUE ? 0 : startTime,
                endTime,
                effectiveTime,
                effectiveDPS: Math.round(totalDamage / effectiveTime),
                raidDPS,
                intervals
            };

            return markRaw(result);
        });

        // Computed stats for the Selected Target Group (Raid View)
        const targetGroupStats = computed(() => {
            return raidStats.value; // Reuse logic
        });

        // Primary target's most recently known HP (current / max), for a compact
        // summary number - a full HP-over-time line on the DPS graph wasn't worth
        // the visual clutter, this single figure is enough.
        const primaryTargetHp = computed(() => {
            const target: any = raidStats.value?.primaryTarget;
            const history = target?.hpHistory || target?.HpHistory;
            if (!history || history.length === 0) return null;
            const last = history[history.length - 1];
            const max = last.maxHp ?? last.MaxHP;
            if (!max || max <= 0) return null;
            const current = last.currentHp ?? last.CurrentHP ?? 0;
            return { current, max };
        });



        const targetStatusStats = computed(() => {
            const stats = raidStats.value;
            if (!stats) return [];

            // Primary Target Entities (from selection)
            const entities = stats.selectedEntities;
            if (!entities.length) return [];

            const mergedTimelines: Record<number, { start: number, end: number }[]> = {};

            // Merge timelines from all selected entities (simplified Union)
            for (const e of entities) {
                const tls = (e as any).statusTimelines || (e as any).StatusTimelines || {};
                const starts = (e as any)._statusStarts || (e as any).statusStarts || (e as any).StatusStarts || {};

                for (const [id, ivs] of Object.entries(tls)) {
                    if (!mergedTimelines[+id]) mergedTimelines[+id] = [];
                    mergedTimelines[+id].push(...(ivs as any));
                }
                for (const [id, start] of Object.entries(starts)) {
                    if (!mergedTimelines[+id]) mergedTimelines[+id] = [];
                    // Backend sends object { time: number, max: ... }, while local eventActor historically might have sent just a number or an object.
                    const startTime = typeof start === 'object' && start !== null ? ((start as any).time ?? (start as any).Time ?? 0) : (start as number);
                    mergedTimelines[+id].push({ start: startTime, end: Number.MAX_VALUE });
                }
            }

            // Group statuses: Map each status ID to its representative ID (first in group)
            const statusIdMap = new Map<number, number>();
            for (const group of statusGroups.value) {
                if (group.statuses.length > 0) {
                    const repId = group.statuses[0]; // First status is representative
                    for (const statusId of group.statuses) {
                        statusIdMap.set(statusId, repId);
                    }
                }
            }

            // Apply grouping: Remap IDs to representative IDs
            const groupedTimelines: Record<number, { start: number, end: number }[]> = {};
            for (const [id, intervals] of Object.entries(mergedTimelines)) {
                const repId = statusIdMap.get(+id) || +id; // Use representative ID or original
                if (!groupedTimelines[repId]) groupedTimelines[repId] = [];
                groupedTimelines[repId].push(...intervals);
            }

            // Base Mask/Clock on Raid Effective Data
            const measureIntervals = toRaw(stats.intervals);
            const measureTime = stats.effectiveTime;

            const statusStats = calculateStatusStatsMainThread(
                groupedTimelines,  // Use grouped timelines instead of merged
                {}, // Starts already merged
                measureIntervals,
                measureTime,
                stats.startTime,
                stats.endTime,
                trackedStatuses.value,
                condNameMap.value
            );

            // Sort by coverage % descending, then by name alphabetically
            statusStats.sort((a: any, b: any) => {
                // First by uptimeRatio descending
                if (b.uptimeRatio !== a.uptimeRatio) {
                    return b.uptimeRatio - a.uptimeRatio;
                }
                // Then by name alphabetically
                return a.name.localeCompare(b.name);
            });

            return markRaw(statusStats);
        });






        // New Session (admin): the server splits the live stream; the live
        // view resets on the marker event it broadcasts (see App.vue).
        const newSessionDialog = ref(false);
        const newSessionBusy = ref(false);
        const newSessionError = ref('');
        const newSessionToast = ref(false);
        const newSessionTable = ref('');
        const startNewSession = async () => {
            newSessionBusy.value = true;
            newSessionError.value = '';
            try {
                const r = await fetch('/api/session/new', { method: 'POST' });
                if (!r.ok) throw new Error(await r.text() || `${r.status}`);
                newSessionTable.value = (await r.json()).table;
                newSessionDialog.value = false;
                newSessionToast.value = true;
            } catch (e) {
                newSessionError.value = `Could not start a new session: ${(e as Error).message}`;
            } finally {
                newSessionBusy.value = false;
            }
        };

        const playerStats = computed(() => {
            // Force re-evaluation on live data ticks so status changes are picked up.
            // statusTimelines and _statusStarts are plain properties without vueUpdateTrack()
            // getters, so Vue cannot track them as dependencies automatically.
            actorManager.value.liveUpdate.value;

            const players = Object.values(activeEntityMap.value).filter((e: any) => (e.isPC || e.IsPC));
            
            // Determine Primary Boss
            let maxDmg = -1;
            for (const id of selectedTargetIds.value) {
                const e = activeEntityMap.value[id];
                if (e) {
                    const totalTake = e.totalTakeDamage ?? e.TotalTakeDamage ?? 0;
                    if (totalTake > maxDmg) {
                        maxDmg = totalTake;
                    }
                }
            }

            // Optimization Path for Players
            const results = players.map((p: any) => {
                let totalDmg = 0;
                let startTime = Number.MAX_VALUE;
                let endTime = 0;
                const combinedTimeline: Record<number, number> = {};

                // Merge Stats
                const agg = p.aggregatedStats || p.AggregatedStats;
                if (!agg) return null;

                const mergedSkills: Record<number, any> = {};
                // The player's pets' skills, kept apart from their own (backend
                // petSkills.go): same skill ids, e.g. Combat Mastery, but never
                // part of the player's crit rate.
                const mergedPetSkills: Record<number, any> = {};

                for (const tid of selectedTargetIds.value) {
                    const tStats = agg[tid];
                    if (tStats) {
                        totalDmg += tStats.totalDamage;
                        if (tStats.firstAttackAt < startTime) startTime = tStats.firstAttackAt;
                        if (tStats.lastAttackAt > endTime) endTime = tStats.lastAttackAt;

                        // Merge timelines
                        for (const [sec, dmg] of Object.entries(tStats.timeline || tStats.Timeline) as [string, number][]) {
                            combinedTimeline[+sec] = (combinedTimeline[+sec] || 0) + dmg;
                        }

                        // Merge Skills. Uses ?? (not ||) for the camelCase/PascalCase
                        // dual-access fallbacks below - a skill can legitimately have
                        // critCount === 0 (no crits landed with it against this
                        // particular target), and 0 is falsy, so `sStats.critCount ||
                        // sStats.CritCount` would incorrectly fall through to the
                        // (nonexistent) PascalCase key and add `undefined`, turning the
                        // running total into NaN the moment a second selected target
                        // contributed a legitimate 0 for that skill.
                        const mergeInto = (mergedSkills: Record<number, any>, skillsMap: Record<string, any>) => {
                        for (const [sid, sStats] of Object.entries(skillsMap) as [string, any][]) {
                            // Passive skills carry a damage split by estimated
                            // trigger skill (backend passiveDamage.go); merge it
                            // per trigger. Copied, not shared: the merge below
                            // mutates the first target's clone.
                            const sTriggers = (sStats.triggers || sStats.Triggers || {}) as Record<string, { damage: number; count: number }>;
                            if (!mergedSkills[+sid]) {
                                const triggers: Record<number, { damage: number; count: number }> = {};
                                for (const [tid, t] of Object.entries(sTriggers)) triggers[+tid] = { damage: t.damage, count: t.count };
                                mergedSkills[+sid] = { ...sStats, min: sStats.min ?? sStats.Min, max: sStats.max ?? sStats.Max, triggers, amp: mergeAmp(sStats.amp, undefined) }; // Clone
                            } else {
                                const mt = mergedSkills[+sid].triggers;
                                for (const [tid, t] of Object.entries(sTriggers)) {
                                    const cur = mt[+tid] ?? (mt[+tid] = { damage: 0, count: 0 });
                                    cur.damage += t.damage;
                                    cur.count += t.count;
                                }
                                const ms = mergedSkills[+sid];
                                ms.amp = mergeAmp(ms.amp, sStats.amp);
                                ms.total += sStats.total ?? sStats.Total ?? 0;
                                ms.count += sStats.count ?? sStats.Count ?? 0;
                                ms.critableCount = (ms.critableCount ?? ms.CritableCount ?? 0) + (sStats.critableCount ?? sStats.CritableCount ?? 0);
                                ms.critCount += sStats.critCount ?? sStats.CritCount ?? 0;
                                const sMin = sStats.min ?? sStats.Min;
                                const sMax = sStats.max ?? sStats.Max;
                                if (sMin < ms.min) ms.min = sMin;
                                if (sMax > ms.max) ms.max = sMax;
                            }
                        }
                        };
                        mergeInto(mergedSkills, tStats.skills || tStats.Skills || {});
                        mergeInto(mergedPetSkills, tStats.petSkills || tStats.PetSkills || {});
                    }
                }

                if (totalDmg === 0) return null;

                // Interval Approx
                const seconds = Object.keys(combinedTimeline).map(Number).sort((a, b) => a - b);
                const targets = selectedTargetIds.value.map(tid => activeEntityMap.value[tid]).filter(e => e);

                const isAnyTargetVulnerable = (at: number) => {
                    if (targets.length === 0) return true;
                    return targets.some((e: any) => {
                        if (e.getConditionState) {
                            const states = e.getConditionState(at);
                            return !states.some((c: any) => c.CCId === 494);
                        }
                        const tls = (e.statusTimelines || e.StatusTimelines) || {};
                        const ivs = tls[494] || tls["494"];
                        if (!ivs) return true;
                        return !ivs.some((iv: any) => at >= iv.start && at <= iv.end);
                    });
                };

                const activeSeconds = seconds.filter(sec => isAnyTargetVulnerable(sec));
                const effectiveTime = Math.max(0.1, activeSeconds.length);
                const activeDPS = Math.round(totalDmg / effectiveTime);

                // Avg DPS uses the raid-shared window (earliest hit by anyone to
                // latest hit by anyone on the selected target(s), from raidStats),
                // not this player's own startTime/endTime - matching Midir's
                // aggregator (targetTimestamps is shared across every attacker of a
                // target) and combat_state.go's raidRawDurationSec, so a late
                // joiner/early leaver isn't diluted or inflated by a tiny personal
                // window.
                const raidRawDuration = Math.max(0.1, raidStats.value?.totalTime || (endTime - startTime) || 0.1);
                const averageDPS = Math.round(totalDmg / raidRawDuration);

                // Status Stats: Use backend-computed stats (includes buff % split and vivace enrichment).
                // The Go backend computes all coverage ratios, splits by mcmbamax %, and enriches
                // with condName/isVivace. Frontend only adds local condNameMap overrides.
                // When targets are selected (and no manual slice is active), prefer the
                // boss-window-clipped override fetched from /api/combat/status-stats so
                // status uptime reflects the selection rather than the full session.
                const rawP = toRaw(p) as any;
                const playerId = rawP.id || rawP.Id || (p as any).id || (p as any).Id;
                let statusStats = (targetWindowStatusStats.value && playerId)
                    ? targetWindowStatusStats.value[playerId]
                    : undefined;
                if (!statusStats) statusStats = rawP.statusStats || rawP.StatusStats;
                if (statusStats && statusStats.length > 0) {
                    // Backend provides: id, key, name, condName, uptime, effectiveUptime,
                    // uptimeRatio, overallRatio, isVivace, maxPercent, magicAttack, etc.
                    // Override display names with local condNameMap if available.
                    statusStats = statusStats
                        .filter((s: any) => trackedStatuses.value.includes(s.id) && (s.uptimeRatio > 0 || s.overallRatio > 0))
                        .map((s: any) => {
                            const localName = condNameMap.value?.[s.id];
                            return {
                                ...s,
                                condName: localName || s.condName || `Status ${s.id}`,
                                // For split buff entries (e.g. "12.5%"), keep the backend name.
                                // For non-split entries, use local name if available.
                                name: s.maxPercent ? s.name : (localName || s.name || `Status ${s.id}`),
                            };
                        });
                } else {
                    // Fallback: client-side calculation (rare â€” only when backend stats unavailable)
                    const rsStart = raidStats.value?.startTime || startTime;
                    const rsEnd = raidStats.value?.endTime || endTime;
                    const raidIntervals = toRaw(raidStats.value?.intervals || []);
                    const effIntervals = raidIntervals.length > 0
                        ? raidIntervals
                        : (rsEnd > rsStart ? [{ start: rsStart, end: rsEnd }] : []);
                    const raidEffTime = raidStats.value?.effectiveTime || effectiveTime;

                    statusStats = calculateStatusStatsMainThread(
                        (p as any).statusTimelines || (p as any).StatusTimelines || {},
                        (p as any)._statusStarts || (p as any).statusStarts || (p as any).StatusStarts || {},
                        effIntervals,
                        raidEffTime,
                        rsStart,
                        rsEnd,
                        trackedStatuses.value,
                        condNameMap.value
                    );

                    // Enrich with display fields for musical buffs
                    statusStats = statusStats.filter((s: any) => (s.uptimeRatio > 0 || s.overallRatio > 0)).map((s: any) => {
                        const isVivace = !!(s.magicAttack || s.alchemicDamage || s.magicCastingSpeed || s.alchemyCastingSpeed);
                        return {
                            ...s,
                            condName: s.name,
                            name: s.maxPercent ? `${s.maxPercent}%` : s.name,
                            isVivace,
                        };
                    });
                }

                // Skill Output. HPM (hits per minute) matches Midir's
                // applyDamageBySkill.vue: count / (window in minutes), using the
                // same raid-shared window as averageDPS so both metrics agree on
                // what "the fight's duration" means.
                const toRow = (s: any, pet: boolean) => ({
                    id: s.id,
                    name: skillNameMap.value?.[s.id] || `Skill ${s.id}`,
                    total: Math.round(s.total),
                    count: s.count,
                    percent: (s.total / totalDmg) * 100,
                    // critRate is crits over critableCount, not count - a pure-DoT
                    // skill (only delayed ticks, e.g. bleed/poison) has
                    // critableCount === 0 (delayed hits can never crit - see the
                    // backend's IsDelayed exclusion) even though count (all hits,
                    // including those ticks) is not, so guard against 0/0 turning
                    // into NaN in the UI.
                    critRate: s.critableCount > 0 ? (s.critCount / s.critableCount) * 100 : 0,
                    critable: s.critableCount > 0,
                    hitsPerMinute: s.count / (raidRawDuration / 60),
                    avg: s.count > 0 ? Math.round(s.total / s.count) : 0,
                    min: Math.round(s.min),
                    max: Math.round(s.max),
                    barPercent: 0,
                    passive: !pet && PASSIVE_SKILL_IDS.has(s.id),
                    pet,
                    // Coverage of its amplifying statuses (Config > Skill Amplification).
                    amp: pet ? undefined : ampCoverage(s.id, s.amp),
                    // Estimated trigger skills of a passive proc, largest first.
                    triggers: Object.entries(s.triggers || {})
                        .map(([tid, t]: [string, any]) => ({
                            id: +tid,
                            name: skillNameMap.value?.[+tid] || `Skill ${tid}`,
                            damage: t.damage,
                            count: t.count,
                            percent: s.total > 0 ? (t.damage / s.total) * 100 : 0,
                        }))
                        .sort((a, b) => b.damage - a.damage),
                });
                const skillStats = [
                    ...Object.values(mergedSkills).map((s: any) => toRow(s, false)),
                    ...Object.values(mergedPetSkills).map((s: any) => toRow(s, true)),
                ].sort((a, b) => b.total - a.total);

                if (skillStats.length > 0) {
                    const maxSkillTotal = skillStats[0].total;
                    skillStats.forEach(s => {
                        s.barPercent = maxSkillTotal > 0 ? (s.total / maxSkillTotal) * 100 : 0;
                    });
                }

                // Active vs passive (proc) vs pet damage, after mabinogi-dilmeter's summary.
                const passiveDamage = skillStats.filter(s => s.passive).reduce((sum, s) => sum + s.total, 0);
                const petDamage = skillStats.filter(s => s.pet).reduce((sum, s) => sum + s.total, 0);
                const activeDamage = skillStats.reduce((sum, s) => sum + s.total, 0) - passiveDamage - petDamage;

                // Overall Crit Rate (Midir's per-player headline critRate): total
                // crits over total crit-eligible hits (critableCount, which
                // excludes delayed/DoT ticks - see the skillStats critRate comment
                // above) across every skill in this view, not an average of each
                // skill's own crit rate.
                let totalHitCount = 0;
                let totalCritCount = 0;
                for (const s of Object.values(mergedSkills) as any[]) {
                    totalHitCount += s.critableCount || 0;
                    totalCritCount += s.critCount || 0;
                }
                const critRate = totalHitCount > 0 ? (totalCritCount / totalHitCount) * 100 : 0;

                return {
                    id: p.id || p.Id,
                    name: getDisplayName(p),
                    entity: p,
                    totalDamage: Math.round(totalDmg),
                    totalHealing: (p as any).totalApplyHealing || (p as any).TotalApplyHealing || 0,
                    effectiveTime,
                    activeDPS,
                    averageDPS,
                    critRate,
                    skillStats,
                    activeDamage,
                    passiveDamage,
                    petDamage,
                    statusStats: markRaw(statusStats)
                };
            }).filter(p => p !== null) as any[];

            // Calculate Raid Total from Aggregated Results
            const aggRaidTotal = Math.max(0, results.reduce((sum, p) => sum + p.totalDamage, 0));

            // Raid Effective Time for "Effective DPS"
            const raidEffectiveTime = Math.max(0.1, raidStats.value?.effectiveTime || 1);

            return markRaw(results.map(p => {
                const percent = (aggRaidTotal > 0 && isFinite(p.totalDamage)) ? (p.totalDamage / aggRaidTotal) * 100 : 0;
                return {
                    ...p,
                    percent: isFinite(percent) ? percent : 0,
                    effectiveDPS: Math.round(p.totalDamage / raidEffectiveTime)
                };
            }).sort((a, b) => b.totalDamage - a.totalDamage));
        });

        // Musical Buffs: Persistent accumulator that only grows (never loses data once seen).
        // Architecture: A stable ref stores the "highest ever seen" values. A watchEffect
        // periodically scans entity data and merges new maximums into it. The displayed
        // list is a simple sorted projection. This prevents the "show then disappear" bug
        // caused by computed() recalculating from scratch and hitting a gap between
        // _statusStarts deletion and statusTimelines population.
        type MusicalBuffEntry = { id: number, name: string, maxPercent?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number };
        const _musicalBuffAccum = ref<Record<number, MusicalBuffEntry>>({});

        // Helper: merge a single value into the accumulator (max-wins)
        const _mergeMusicalBuff = (id: number, name: string, vals: { maxPercent?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number }) => {
            const acc = _musicalBuffAccum.value;
            if (!acc[id]) acc[id] = { id, name };
            const e = acc[id];
            if (name && (e.name === `Status ${id}` || !e.name)) e.name = name;
            if (vals.maxPercent !== undefined && (e.maxPercent === undefined || vals.maxPercent > e.maxPercent)) e.maxPercent = vals.maxPercent;
            if (vals.magicAttack !== undefined && (e.magicAttack === undefined || vals.magicAttack > e.magicAttack)) e.magicAttack = vals.magicAttack;
            if (vals.alchemicDamage !== undefined && (e.alchemicDamage === undefined || vals.alchemicDamage > e.alchemicDamage)) e.alchemicDamage = vals.alchemicDamage;
            if (vals.magicCastingSpeed !== undefined && (e.magicCastingSpeed === undefined || vals.magicCastingSpeed > e.magicCastingSpeed)) e.magicCastingSpeed = vals.magicCastingSpeed;
            if (vals.alchemyCastingSpeed !== undefined && (e.alchemyCastingSpeed === undefined || vals.alchemyCastingSpeed > e.alchemyCastingSpeed)) e.alchemyCastingSpeed = vals.alchemyCastingSpeed;
        };

        // Scan entities and merge into accumulator on every reactive tick
        watchEffect(() => {
            // Reactive dependencies: entity map + live update tick
            actorManager.value.liveUpdate.value;
            // Restrict to actual party/combat participants (players who have dealt damage
            // this session), not every PC entity the client happened to see nearby -
            // e.g. unrelated players passing through or fighting the same open-world target.
            const players = Object.values(activeEntityMap.value).filter((e: any) => {
                if (!(e.isPC || e.IsPC)) return false;
                const dealt = e.totalApplyDamage ?? e.TotalApplyDamage ?? 0;
                return dealt > 0;
            });

            for (const p of players) {
                if (!p) continue;
                const rawP = toRaw(p) as any;

                const timelines = rawP.statusTimelines || rawP.StatusTimelines || {};
                const starts = rawP._statusStarts || rawP.statusStarts || rawP.StatusStarts || {};
                const backendStats = rawP.statusStats || rawP.StatusStats;

                const allIds = new Set<number>();
                Object.keys(timelines).forEach(k => allIds.add(+k));
                Object.keys(starts).forEach(k => allIds.add(+k));
                if (backendStats) backendStats.forEach((s: any) => allIds.add(s.id));

                for (const id of allIds) {
                    let maxPercent: number | undefined;
                    let magicAttack: number | undefined;
                    let alchemicDamage: number | undefined;
                    let magicCastingSpeed: number | undefined;
                    let alchemyCastingSpeed: number | undefined;

                    const ivs = timelines[id] || timelines[String(id)] || [];
                    for (const iv of ivs) {
                        if (iv.max !== undefined && (maxPercent === undefined || iv.max > maxPercent)) maxPercent = iv.max;
                        if (iv.magicAttack !== undefined && (magicAttack === undefined || iv.magicAttack > magicAttack)) magicAttack = iv.magicAttack;
                        if (iv.alchemicDamage !== undefined && (alchemicDamage === undefined || iv.alchemicDamage > alchemicDamage)) alchemicDamage = iv.alchemicDamage;
                        if (iv.magicCastingSpeed !== undefined && (magicCastingSpeed === undefined || iv.magicCastingSpeed > magicCastingSpeed)) magicCastingSpeed = iv.magicCastingSpeed;
                        if (iv.alchemyCastingSpeed !== undefined && (alchemyCastingSpeed === undefined || iv.alchemyCastingSpeed > alchemyCastingSpeed)) alchemyCastingSpeed = iv.alchemyCastingSpeed;
                    }

                    const st = starts[id] || starts[String(id)];
                    if (st) {
                        if (st.max !== undefined && (maxPercent === undefined || st.max > maxPercent)) maxPercent = st.max;
                        if (st.magicAttack !== undefined && (magicAttack === undefined || st.magicAttack > magicAttack)) magicAttack = st.magicAttack;
                        if (st.alchemicDamage !== undefined && (alchemicDamage === undefined || st.alchemicDamage > alchemicDamage)) alchemicDamage = st.alchemicDamage;
                        if (st.magicCastingSpeed !== undefined && (magicCastingSpeed === undefined || st.magicCastingSpeed > magicCastingSpeed)) magicCastingSpeed = st.magicCastingSpeed;
                        if (st.alchemyCastingSpeed !== undefined && (alchemyCastingSpeed === undefined || st.alchemyCastingSpeed > alchemyCastingSpeed)) alchemyCastingSpeed = st.alchemyCastingSpeed;
                    }

                    if (backendStats) {
                        const bStat = backendStats.find((s: any) => s.id === id);
                        if (bStat) {
                            if (bStat.maxPercent !== undefined && (maxPercent === undefined || bStat.maxPercent > maxPercent)) maxPercent = bStat.maxPercent;
                            if (bStat.magicAttack !== undefined && (magicAttack === undefined || bStat.magicAttack > magicAttack)) magicAttack = bStat.magicAttack;
                            if (bStat.alchemicDamage !== undefined && (alchemicDamage === undefined || bStat.alchemicDamage > alchemicDamage)) alchemicDamage = bStat.alchemicDamage;
                            if (bStat.magicCastingSpeed !== undefined && (magicCastingSpeed === undefined || bStat.magicCastingSpeed > magicCastingSpeed)) magicCastingSpeed = bStat.magicCastingSpeed;
                            if (bStat.alchemyCastingSpeed !== undefined && (alchemyCastingSpeed === undefined || bStat.alchemyCastingSpeed > alchemyCastingSpeed)) alchemyCastingSpeed = bStat.alchemyCastingSpeed;
                        }
                    }

                    if (maxPercent !== undefined || magicAttack !== undefined || alchemicDamage !== undefined || magicCastingSpeed !== undefined || alchemyCastingSpeed !== undefined) {
                        _mergeMusicalBuff(id, condNameMap.value?.[id] || `Status ${id}`, { maxPercent, magicAttack, alchemicDamage, magicCastingSpeed, alchemyCastingSpeed });
                    }
                }
            }
        });

        // Reset accumulator on clear
        watch(() => generationCounter.value, () => {
            _musicalBuffAccum.value = {};
        });

        // Sorted read-only projection for the template
        const raidMusicalBuffs = computed(() => {
            return Object.values(_musicalBuffAccum.value)
                .filter(b => b.maxPercent !== undefined || b.magicAttack !== undefined || b.alchemicDamage !== undefined || b.magicCastingSpeed !== undefined || b.alchemyCastingSpeed !== undefined)
                .sort((a, b) => (b.maxPercent || b.magicAttack || 0) - (a.maxPercent || a.magicAttack || 0));
        });

        function formatDurationNearestMin(sec: number) {
            return `${Math.round(sec / 60)} min`;
        }

        function formatEntityId(id: string) {
            if (!id) return 'Unknown';
            // Ensure we have at least 6 characters to split 2...4
            if (id.length < 6) return id;
            return `${id.substring(0, 2)}...${id.substring(id.length - 4)}`;
        }



        onMounted(() => {
            appEvent.value.addEventListener('clear', onClearHandler);

            // Lazy Load Resources (Non-blocking)
            // Guard: If data is already loaded in the Store (singleton), DON'T overwrite it.
            // This prevents forced reactivity updates which cause v-img flash/blank.
            if (Object.keys(skillNameMap.value).length > 0 && Object.keys(condNameMap.value).length > 0) {
                // Already loaded, do nothing
                return;
            }

            // Even if App.vue loaded them, we check again to be safe/reactive
            Promise.all([
                db.value.getData('SkillNameMap'),
                db.value.getData('StatusNameMap'),
                db.value.getData('RaceNameMap')
            ]).then(([localSkillMap, localCondMap, localRaceMap]) => {
                // Double check inside promise in case multiple components ran this
                if (localSkillMap && Object.keys(skillNameMap.value).length === 0) Object.assign(skillNameMap.value, localSkillMap);
                if (localCondMap && Object.keys(condNameMap.value).length === 0) Object.assign(condNameMap.value, localCondMap);
                if (localRaceMap && Object.keys(raceNameMap.value).length === 0) {
                    for (const [id, name] of Object.entries(localRaceMap)) {
                        raceNameMap.value[+id] = `${name} ${id}`;
                    }
                }
            }).catch((e: any) => console.error("Failed to lazy load analysis resources", e));
        });

        // Per-player hit log toggle: tracks which player+target is currently showing the damage log
        const activeDamageLog = ref<{ playerId: string; targetId: string } | null>(null);
        // Hit log modal open state
        const hitLogOpen = ref(false);
        // Reset damage log panel when target selection changes
        watch(selectedTargetIds, () => { activeDamageLog.value = null; hitLogOpen.value = false; });

        return {
            selectedTargetIds,
            targetList,
            primaryTargetName,
            targetGroupStats,
            primaryTargetHp,
            targetStatusStats,
            playerStats,
            raidMusicalBuffs,
            playerHeaders,
            anonymizeMode,
            currentUser, newSessionDialog, newSessionBusy, newSessionError, newSessionToast, newSessionTable, startNewSession,
            region,
            getPrimaryClass,
            getPrimaryWeapon,
            getSkillIconUrl,
            getStatusIconUrl,
            getPlayerColor,
            getSkillColor,
            formatDuration,
            formatDurationNearestMin,
            formatEntityId,
            hexToRgba,
            formatNumber,
            raceNameMap,
            trackedBosses,
            // Timeline slice
            combatBounds,
            targetDead,
            status803Intervals,
            fetchSlice,
            activeSlice,
            // Damage Log panel
            activeDamageLog,
            hitLogOpen,
        };
    }
});
</script>

<style scoped>
/* Player header: plain row with the damage share as a thin bar along the bottom. */
.share-row {
    position: relative;
    background: rgba(0, 0, 0, 0.3);
}

.share-bar {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 4px;
    border-radius: 0 2px 2px 0;
    pointer-events: none;
}

/* Expansion panel override to remove default padding and styling that interferes with table look */
:deep(.v-expansion-panel-text__wrapper) {
    padding: 0 !important;
}

:deep(.v-expansion-panel--active > .v-expansion-panel-title) {
    min-height: auto !important;
}

.v-expansion-panel-title__overlay {
    background-color: transparent !important;
}

/* Ensure text contrasts */
.text-grey-lighten-1 {
    color: #bdbdbd !important;
}

.text-grey-lighten-2 {
    color: #e0e0e0 !important;
}
</style>
